import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import WebSocketInstance from "./common/WebSocketInstance";
import { isPublicSlug, fetchAndStorePublicToken } from "./common/publicUtils";
import { isSessionDisconnected } from "./common/tokenRefresh";

interface RequireAuthProps {
  children: React.ReactNode;
}

const RequireAuth: React.FC<RequireAuthProps> = ({ children }) => {
  const isAuthenticated = localStorage.getItem("JMLAuthToken");
  const isDevEnvironment = process.env.REACT_APP_ENVIRONMENT === "dev";
  const location = useLocation();

  const canvasMatch = location.pathname.match(/\/canvas\/([^/]+)/);
  const slugOrIdFromUrl = canvasMatch ? canvasMatch[1] : null;
  const isPublic = slugOrIdFromUrl ? isPublicSlug(slugOrIdFromUrl) : false;

  const initialFetching = isPublic && !isAuthenticated && !localStorage.getItem("JMLPublicToken");
  const [isTokenFetching, setIsTokenFetching] = React.useState(initialFetching);
  const [, setHasToken] = React.useState(!!isAuthenticated || !!localStorage.getItem("JMLPublicToken"));

  React.useEffect(() => {
    const fetchToken = async () => {
      const publicToken = localStorage.getItem("JMLPublicToken");
      if (isPublic && !isAuthenticated && !publicToken) {
        setIsTokenFetching(true);
        const token = await fetchAndStorePublicToken();
        if (token) {
          setHasToken(true);
        }
        setIsTokenFetching(false);
      } else if (isAuthenticated || publicToken) {
        setHasToken(true);
        setIsTokenFetching(false);
      }
    };
    fetchToken();
  }, [isPublic, isAuthenticated]);

  if (isTokenFetching) {
    return <div style={{ minHeight: "100vh", background: "black" }}></div>;
  }

  // A disconnected session keeps its stored token until the user acknowledges the prompt, so every
  // guard below would still see "logged in". Hold the route and let the modal be the only way out:
  // redirecting to /login would bounce straight back here (that guard trusts the stored token) and
  // the two would loop until the tab is unresponsive.
  if (isSessionDisconnected()) {
    return <>{children}</>;
  }

  if (!isAuthenticated && !isPublic) {
    const returnTo = `${location.pathname}${location.search}${location.hash}`;
    return <Navigate to="/login" replace state={{ path: returnTo }} />;
  }

  // If public, we should have the public token by now if it was needed
  const wsInstance = WebSocketInstance.getInstance();

  if (!wsInstance.isSocketOpen && !isPublic) {
    const returnTo = `${location.pathname}${location.search}${location.hash}`;
    return <Navigate to="/login" replace state={{ path: returnTo }} />;
  }

  if (isDevEnvironment) {
    console.log(isAuthenticated ? "Authenticated" : "Public Access");
    console.log("Children:", children);
  }

  return <>{children}</>;
};

export default RequireAuth;
